import { useState } from "react";
import { fmt, type Format } from "../lib/reporting";

/* Chart primitives for the dashboard. Built to the dataviz method:
 * thin marks, 4px-rounded data ends on the baseline, 2px gaps, recessive
 * grid/axes, hover tooltips, a legend for ≥2 series, and text in ink tokens
 * (never the series color). Palette is validated in lib/reporting.ts. */

type Datum = { key: string; value: number; color: string };

function useTip() {
  const [tip, setTip] = useState<{ x: number; y: number; label: string; value: string } | null>(null);
  const node = tip ? (
    <div className="chart-tip" style={{ left: tip.x, top: tip.y }}>
      <span>{tip.label}</span>
      <b>{tip.value}</b>
    </div>
  ) : null;
  return { tip, setTip, node };
}

/* ---------------- metric (hero number, no plot) ---------------- */
export function MetricTile({ label, value, format, foot }: { label: string; value: number; format: Format; foot?: string }) {
  return (
    <div className="stat">
      <div className="stat__label">{label}</div>
      <div className="stat__value">{fmt(value, format)}</div>
      {foot && <div className="stat__foot">{foot}</div>}
    </div>
  );
}

/* ---------------- horizontal bar ---------------- */
export function BarChart({ data, format }: { data: Datum[]; format: Format }) {
  const max = Math.max(1, ...data.map((d) => d.value));
  const { setTip, node } = useTip();
  if (!data.length) return <div className="empty">No data</div>;
  return (
    <div className="chart-wrap">
      {node}
      <div className="bars">
        {data.map((d) => (
          <div
            className="bar-row"
            key={d.key}
            onMouseMove={(e) => {
              const r = (e.currentTarget.closest(".chart-wrap") as HTMLElement).getBoundingClientRect();
              setTip({ x: e.clientX - r.left + 12, y: e.clientY - r.top - 8, label: d.key, value: fmt(d.value, format) });
            }}
            onMouseLeave={() => setTip(null)}
          >
            <span className="bar-label" title={d.key}>{d.key}</span>
            <span className="bar-track">
              <i style={{ width: `${(d.value / max) * 100}%`, background: d.color }} />
            </span>
            <span className="bar-val num">{fmt(d.value, format)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- donut ---------------- */
export function DonutChart({ data, format }: { data: Datum[]; format: Format }) {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  const [hover, setHover] = useState<number | null>(null);
  const size = 168, stroke = 26, r = (size - stroke) / 2, c = 2 * Math.PI * r;
  const gap = 2 / (2 * Math.PI * r) * 360; // ~2px gap in degrees
  let acc = 0;
  const center = hover != null ? data[hover] : null;

  return (
    <div className="donut-wrap">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: "rotate(-90deg)" }}>
        {data.map((d, i) => {
          const frac = d.value / total;
          const len = frac * c;
          const seg = (
            <circle
              key={d.key}
              cx={size / 2} cy={size / 2} r={r} fill="none"
              stroke={d.color} strokeWidth={hover === i ? stroke + 4 : stroke}
              strokeDasharray={`${Math.max(0, len - (gap / 360) * c)} ${c}`}
              strokeDashoffset={-acc}
              opacity={hover == null || hover === i ? 1 : 0.4}
              onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
              style={{ transition: "stroke-width .12s, opacity .12s", cursor: "pointer" }}
            />
          );
          acc += len;
          return seg;
        })}
      </svg>
      <div className="donut-center">
        <b>{center ? fmt(center.value, format) : fmt(total, format)}</b>
        <small>{center ? center.key : "Total"}</small>
      </div>
      <Legend data={data} format={format} onHover={setHover} />
    </div>
  );
}

export function Legend({ data, format, onHover }: { data: Datum[]; format: Format; onHover?: (i: number | null) => void }) {
  return (
    <div className="legend">
      {data.map((d, i) => (
        <div className="legend-item" key={d.key} onMouseEnter={() => onHover?.(i)} onMouseLeave={() => onHover?.(null)}>
          <span className="legend-sw" style={{ background: d.color }} />
          <span className="legend-label">{d.key}</span>
          <span className="legend-val num">{fmt(d.value, format)}</span>
        </div>
      ))}
    </div>
  );
}

/* ---------------- line (single series, crosshair + tooltip) ---------------- */
export function LineChart({ points, labels, format }: { points: number[]; labels: string[]; format: Format }) {
  const w = 520, h = 150, padX = 8, padY = 14;
  const [hi, setHi] = useState<number | null>(null);
  const max = Math.max(...points, 100), min = Math.min(...points, 0);
  const span = max - min || 1;
  const x = (i: number) => padX + (i / (points.length - 1)) * (w - padX * 2);
  const y = (v: number) => h - padY - ((v - min) / span) * (h - padY * 2);
  const line = points.map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(1)} ${y(p).toFixed(1)}`).join(" ");
  const area = `${line} L${x(points.length - 1)} ${h} L${x(0)} ${h} Z`;

  return (
    <div className="chart-wrap">
      <svg
        width="100%" height={168} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ overflow: "visible", display: "block" }}
        onMouseMove={(e) => {
          const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
          const rel = ((e.clientX - rect.left) / rect.width) * w;
          const i = Math.round(((rel - padX) / (w - padX * 2)) * (points.length - 1));
          setHi(Math.max(0, Math.min(points.length - 1, i)));
        }}
        onMouseLeave={() => setHi(null)}
      >
        {/* recessive baseline */}
        <line x1={0} y1={h - padY} x2={w} y2={h - padY} stroke="var(--line)" strokeWidth={1} />
        <defs>
          <linearGradient id="lc" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--corona)" stopOpacity="0.16" />
            <stop offset="100%" stopColor="var(--corona)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#lc)" />
        <path d={line} fill="none" stroke="var(--corona)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        {hi != null && (
          <>
            <line x1={x(hi)} y1={padY} x2={x(hi)} y2={h - padY} stroke="var(--line)" strokeWidth={1} strokeDasharray="3 3" />
            <circle cx={x(hi)} cy={y(points[hi])} r={4.5} fill="var(--corona)" stroke="var(--surface)" strokeWidth={2} />
          </>
        )}
      </svg>
      <div className="line-x">
        {labels.map((l, i) => (l ? <span key={i} style={{ left: `${(i / (points.length - 1)) * 100}%` }}>{l}</span> : null))}
      </div>
      {hi != null && (
        <div className="chart-tip" style={{ left: `${(hi / (points.length - 1)) * 100}%`, top: 0 }}>
          <span>week {hi + 1}</span><b>{fmt(points[hi], format)}</b>
        </div>
      )}
    </div>
  );
}

/* ---------------- table ---------------- */
export function ReportTable({ columns, rows }: { columns: string[]; rows: { name: string; cells: string[] }[] }) {
  return (
    <table className="table">
      <thead><tr>{columns.map((c, i) => <th key={c} className={i > 1 ? "num" : ""}>{c}</th>)}</tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.name}>
            <td><b style={{ fontWeight: 600 }}>{r.name}</b></td>
            {r.cells.map((c, i) => <td key={i} className={i > 0 ? "num" : ""}>{c}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
